import { assertNonEmpty } from '../shared/branded-id';
import type { OrgId, UserId } from '../shared/ids';
import type { OrderLineItem, PricingSnapshot } from './order-line-item';

export type CoverageRequestStatus = 'pending' | 'in_review' | 'approved' | 'rejected' | 'cancelled';

export type CoverageRequestId = string & { readonly __brand: 'CoverageRequestId' };

export function CoverageRequestId(raw: string): CoverageRequestId {
  return assertNonEmpty(raw.trim(), raw, 'CoverageRequestId') as CoverageRequestId;
}

/**
 * Line items and the pricing snapshot are parsed at the repository boundary
 * (see order-line-item.ts), so a stored row that does not match the wire
 * shape fails the read instead of reaching the views as if it were typed.
 */
export type CoverageRequest = {
  readonly kind: 'coverage-request';
  readonly id: CoverageRequestId;
  readonly orgId: OrgId;
  readonly requestedBy: UserId;
  readonly requestedAt: string;
  readonly status: CoverageRequestStatus;
  readonly statusUpdatedAt: string | null;
  readonly items: readonly OrderLineItem[];
  readonly pricingSnapshot: PricingSnapshot | null;
  readonly adminNotes: string | null;
  readonly resolvedBy: UserId | null;
  readonly resolvedAt: string | null;
};

const STATUS_TRANSITIONS: Readonly<
  Record<CoverageRequestStatus, readonly CoverageRequestStatus[]>
> = {
  pending: ['in_review', 'rejected'],
  in_review: ['approved', 'rejected'],
  approved: [],
  rejected: ['pending'],
  cancelled: [],
};

export function allowedTransitionsFrom(
  status: CoverageRequestStatus
): readonly CoverageRequestStatus[] {
  return STATUS_TRANSITIONS[status];
}

export function canTransition(from: CoverageRequestStatus, to: CoverageRequestStatus): boolean {
  return STATUS_TRANSITIONS[from].includes(to);
}

export function isActionable(req: CoverageRequest): boolean {
  return req.status === 'pending' || req.status === 'in_review';
}
